
import { Component, signal, inject, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { GamificationService } from './services/gamification.service';
import { MarkdownModule } from 'ngx-markdown';

interface GameState {
  day: number;
  money: number;
  reputation: number;
  totalCustomers: number;
  history: SimulationResult[];
}

interface SimulationResult {
  day: number;
  revenue: number;
  cost: number;
  profit: number;
  analysis: string;
  reviews: string[];
}

interface StrategyOption {
  label: string;
  value: string;
  cost: number;
}

type GameStatus = 'playing' | 'won' | 'lost';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './app.component.html',
  styleUrls: [] 
})
export class AppComponent {
  private gameService = inject(GamificationService);

  // UI State
  showTutorial = signal(true);
  gameStatus = signal<GameStatus>('playing');
  isLoading = signal(false);
  showResultModal = signal(false);
  lastResult = signal<any>(null);

  // Game State
  gameState = signal<GameState>({
    day: 1,
    money: 10000, // Starting capital
    reputation: 1,
    totalCustomers: 0,
    history: []
  });

  // --- Configuration Options with COSTS ---
  
  goalOptions: StrategyOption[] = [
    { label: 'สะสมแต้มครบ 10 ดวง รับแก้วฟรี', value: 'Buy 10 get 1 free loyalty card', cost: 500 },
    { label: 'ซื้อครบ 500 บาท เป็น VIP', value: 'Spend 500 THB to unlock VIP status', cost: 2000 },
    { label: 'ลองเครื่องดื่มใหม่ครบ 4 เมนู', value: 'Try 4 seasonal drinks quest', cost: 1500 }
  ];
  selectedGoal = signal(this.goalOptions[0]);

  ruleOptions: StrategyOption[] = [
    { label: 'ต้องสมัครสมาชิกด้วยเบอร์โทร', value: 'Must register with phone number', cost: 800 },
    { label: 'ต้องถ่ายรูปเช็คอินร้าน', value: 'Must take photo and check-in', cost: 0 },
    { label: 'ไม่มีเงื่อนไขยุ่งยาก แค่ซื้อก็พอ', value: 'No complex rules, just purchase', cost: 0 }
  ];
  selectedRule = signal(this.ruleOptions[0]);

  conflictOptions: StrategyOption[] = [
    { label: 'Leaderboard: ใครกินเยอะสุด (จอแสดงผล)', value: 'Weekly Leaderboard competition', cost: 3000 },
    { label: 'Co-op: ลูกค้าทุกคนช่วยกัน (บอร์ดแปะ)', value: 'Community goal cooperation', cost: 500 },
    { label: 'Challenge: ท้าให้ลองเมนูแปลก (ค่าวัตถุดิบ)', value: 'Self-challenge spicy coffee', cost: 1000 }
  ];
  selectedConflict = signal(this.conflictOptions[1]);

  timeOptions: StrategyOption[] = [
    { label: 'โปรโมชั่นตลอดทั้งปี (ค่าการตลาดสูง)', value: 'Year-round promotion', cost: 2000 },
    { label: 'Happy Hour: 14:00 - 16:00 (เสียโอกาสขายราคาเต็ม)', value: 'Happy Hour limited time', cost: 1000 },
    { label: 'Flash Sale 3 วัน (เร่งด่วน)', value: '3-day flash event', cost: 500 }
  ];
  selectedTime = signal(this.timeOptions[2]);

  rewardOptions: StrategyOption[] = [
    { label: 'ส่วนลด 50% (ขาดทุนกำไรมาก)', value: '50% Discount', cost: 4000 },
    { label: 'ตราประทับดิจิทัล (Badge)', value: 'Digital Collector Badge', cost: 200 },
    { label: 'สิทธิ์ในการตั้งชื่อเมนูใหม่', value: 'Status reward: Name a menu item', cost: 0 }
  ];
  selectedReward = signal(this.rewardOptions[1]);

  feedbackOptions: StrategyOption[] = [
    { label: 'App แจ้งเตือนทันที (ค่าระบบ)', value: 'Instant push notification progress bar', cost: 2500 },
    { label: 'สรุปยอดทางอีเมล (รายสัปดาห์)', value: 'Weekly email summary', cost: 500 },
    { label: 'พนักงานแจ้งปากเปล่า', value: 'Verbal feedback from staff', cost: 0 }
  ];
  selectedFeedback = signal(this.feedbackOptions[2]);

  levelOptions: StrategyOption[] = [
    { label: '3 ระดับ: Bronze, Silver, Gold (บัตรแข็ง)', value: '3-tier system: Bronze, Silver, Gold', cost: 1500 },
    { label: 'ระดับเดียว (ไม่ต้องจัดการ)', value: 'Flat structure, no levels', cost: 0 },
    { label: 'Level ไม่จำกัด (ระบบคำนวณซับซ้อน)', value: 'Infinite numeric levels', cost: 3000 }
  ];
  selectedLevel = signal(this.levelOptions[1]);

  // Derived State
  currentLevelTitle = computed(() => {
    const rep = this.gameState().reputation;
    if (rep < 3) return 'รถเข็นขายกาแฟ (Street Cart)';
    if (rep < 6) return 'ร้านกาแฟเล็กๆ (Small Cafe)';
    if (rep < 9) return 'คาเฟ่สุดหรู (Luxury Cafe)';
    return 'อาณาจักรกาแฟ (Coffee Empire)';
  });

  currentShopIcon = computed(() => {
    const rep = this.gameState().reputation;
    if (rep < 3) return '🚲';
    if (rep < 6) return '🏪';
    if (rep < 9) return '🏯';
    return '🏰';
  });

  // Calculate total weekly cost based on selected strategies
  totalWeeklyCost = computed(() => {
    return this.selectedGoal().cost +
           this.selectedRule().cost +
           this.selectedConflict().cost +
           this.selectedTime().cost +
           this.selectedReward().cost +
           this.selectedFeedback().cost +
           this.selectedLevel().cost;
  });

  canAfford = computed(() => {
    return this.gameState().money >= this.totalWeeklyCost();
  });

  toggleTutorial() {
    this.showTutorial.update(v => !v);
  }

  restartGame() {
    this.gameState.set({
      day: 1,
      money: 10000,
      reputation: 1,
      totalCustomers: 0,
      history: []
    });
    this.gameStatus.set('playing');
    this.showTutorial.set(true);
    this.showResultModal.set(false);
  }

  async runSimulation() {
    if (this.isLoading() || this.gameStatus() !== 'playing') return;
    
    if (!this.canAfford()) {
      alert('เงินทุนไม่พอสำหรับแผนนี้! กรุณาลดต้นทุนโดยเปลี่ยนกลยุทธ์');
      return;
    }

    this.isLoading.set(true);
    const investment = this.totalWeeklyCost();

    // Deduct money immediately (Investment)
    this.gameState.update(state => ({
      ...state,
      money: state.money - investment
    }));

    const inputs = {
      goal: this.selectedGoal().value,
      rules: this.selectedRule().value,
      conflict: this.selectedConflict().value,
      time: this.selectedTime().value,
      reward: this.selectedReward().value,
      feedback: this.selectedFeedback().value,
      levels: this.selectedLevel().value,
      investment: investment
    };

    try {
      const result = await this.gameService.simulateTurn(
        this.gameState().day,
        this.gameState().money,
        this.gameState().reputation,
        inputs
      );

      const profit = result.revenue - investment;
      const resultWithProfit = { ...result, cost: investment, profit };

      this.lastResult.set(resultWithProfit);
      
      this.gameState.update(state => {
        const newMoney = state.money + result.revenue;
        const newReputation = Math.min(10, Math.max(1, state.reputation + result.reputationChange));
        
        // Check Game End Conditions
        if (newMoney < 0) {
          this.gameStatus.set('lost');
        } else if (newReputation >= 10) {
          this.gameStatus.set('won');
        }

        return {
          day: state.day + 7,
          money: newMoney,
          reputation: newReputation,
          totalCustomers: state.totalCustomers + result.newCustomers,
          history: [
            ...state.history, 
            { 
              day: state.day, 
              revenue: result.revenue,
              cost: investment,
              profit: profit,
              analysis: result.educationalAnalysis,
              reviews: result.customerReviews 
            }
          ]
        };
      });

      this.showResultModal.set(true);

    } catch (e) {
      alert("เกิดข้อผิดพลาดในการจำลองสถานการณ์: " + e);
      this.gameState.update(state => ({ ...state, money: state.money + investment }));
    } finally {
      this.isLoading.set(false);
    }
  }

  closeModal() {
    this.showResultModal.set(false);
  }

  getReputationWidth() {
    return `${(this.gameState().reputation / 10) * 100}%`;
  }
}
